import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EmployeeService } from '../../services/employee.service';
import { CreateShiftRequest } from '../../models/employee.models';

export interface ShiftDay {
  day: string;
  status: 'Working' | 'Day off';
  startTime: string;
  endTime: string;
}

export interface AddShiftFormData {
  shift_name: string;
  days: ShiftDay[];
}

/**
 * Returns a default AddShiftFormData object pre-filled with an empty shift name and a week of day entries (Monday–Friday as Working, Saturday–Sunday as Day off) each with start and end times.
 * @example
 * getDefaultShiftForm()
 * {
 *   shift_name: '',
 *   days: [
 *     { day: 'Monday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
 *     { day: 'Tuesday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
 *     { day: 'Wednesday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
 *     { day: 'Thursday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
 *     { day: 'Friday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
 *     { day: 'Saturday', status: 'Day off', startTime: '8:00 AM', endTime: '8:00 PM' },
 *     { day: 'Sunday', status: 'Day off', startTime: '8:00 AM', endTime: '8:00 PM' }
 *   ]
 * }
 * @returns {AddShiftFormData} Default shift form data object.
 */
function getDefaultShiftForm(): AddShiftFormData {
  return {
    shift_name: '',
    days: [
      { day: 'Monday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
      { day: 'Tuesday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
      { day: 'Wednesday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
      { day: 'Thursday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
      { day: 'Friday', status: 'Working', startTime: '8:00 AM', endTime: '8:00 PM' },
      { day: 'Saturday', status: 'Day off', startTime: '8:00 AM', endTime: '8:00 PM' },
      { day: 'Sunday', status: 'Day off', startTime: '8:00 AM', endTime: '8:00 PM' },
    ],
  };
}

/**
 * Modal component for creating and editing employee shift schedules; provides a form-driven UI to set per-day working status and start/end times, validates input, and communicates with an EmployeeService to load, create, or update shifts.
 * @component
 * @selector app-add-shift-modal
 * @example
 *   <app-add-shift-modal
 *     [isVisible]="true"
 *     [editMode]="true"
 *     [editShiftId]="'shift-123'"
 *     [editData]="shiftFormData"
 *     (closeModal)="handleClose()"
 *     (saveShift)="handleSave($event)">
 *   </app-add-shift-modal>
 * @input boolean isVisible - Controls modal visibility; when true the modal is shown and the component may load data for editing.
 * @input boolean editMode - When true the component operates in edit mode (updates an existing shift) versus create mode.
 * @input string|null editShiftId - The ID of the shift to load for editing; used to fetch shift details from the API when present.
 * @input AddShiftFormData|null editData - Optional preloaded form data used to populate the form when editing (falls back to API data or defaults).
 * @output EventEmitter<void> closeModal - Emitted when the modal is closed or cancelled.
 * @output EventEmitter<AddShiftFormData> saveShift - Emitted after a successful create or update operation with the saved form data.
 *
 * Purpose and behavior:
 *   - Presents a form that models a shift with a name and per-day configuration (Working / Day off, start and end times).
 *   - Provides client-side validation (e.g., shift name max length 100) and prevents editing while submitting.
 *   - Generates 12-hour time options for dropdowns and converts times between 12-hour display and 24-hour API formats.
 *   - In edit mode it can load shift details from the EmployeeService, populate the form, and call update; in create mode it calls create.
 *
 * Lifecycle hooks:
 *   - ngOnChanges(changes: SimpleChanges): reacts to changes in isVisible, editMode, editShiftId, and editData. Clears errors when the modal opens, loads shift details from the API if an edit ID is provided, populates the form with provided editData, or resets to defaults when not editing.
 *
 * Key methods:
 *   - loadShiftDetails(): Loads shift and day data from EmployeeService for a given editShiftId, handles loading and API error messages.
 *   - populateFormFromApiData(data): Maps API response (shift and days) into the component's AddShiftFormData structure with sensible defaults.
 *   - generateTimeOptions(): Builds the list of 12-hour formatted time strings (every 30 minutes) used by time dropdowns.
 *   - convertTimeTo12Hour(time24: string): Converts a "HH:MM:SS" 24-hour string to "h:mm AM/PM" for display.
 *   - convertTimeTo24Hour(time12Hour: string): Converts "h:mm AM/PM" into "HH:MM" for API payloads.
 *   - transformDataForApi(): Transforms the internal form representation into the CreateShiftRequest shape required by the API.
 *   - onSave(): Validates form data, toggles submitting state, calls create or update via EmployeeService, handles success by emitting saveShift and closing, and surfaces API error messages.
 *   - onCancel(): Resets the form and closes the modal without saving.
 *   - onClose(): Closes dropdowns and emits closeModal.
 *   - onModalClick(event): Stops propagation to prevent modal background click from closing when interacting with modal content.
 *   - onShiftNameKeydown(event): Prevents typing beyond 100 characters and sets a user-visible shift name error.
 *   - onShiftNameInput(event): Handles input/paste events to truncate and validate shift name length and manage error state.
 *   - onDayStatusChange(dayIndex, status): Updates a day's status (Working / Day off), and closes related dropdowns when toggled to Day off.
 *   - onDayStartTimeChange(dayIndex, time) / onDayEndTimeChange(dayIndex, time): Update day's start/end times and close the corresponding dropdown.
 *   - toggleStatusDropdown(dayIndex), toggleStartTimeDropdown(dayIndex), toggleEndTimeDropdown(dayIndex): Manage custom dropdown open/close state while respecting isSubmitting and day status.
 *   - resetForm(): Restores the form to default values, clears errors, and closes any open dropdowns.
 *   - closeAllDropdowns(): Utility to close any of the component's open dropdowns.
 */
@Component({
  selector: 'app-add-shift-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './add-shift-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-shift-modal.component.scss'],
})
export class AddShiftModalComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() editMode = false;
  @Input() editShiftId: string | null = null;
  @Input() editData: AddShiftFormData | null = null;
  @Output() closeModal = new EventEmitter<void>();
  @Output() saveShift = new EventEmitter<AddShiftFormData>();

  shiftForm: AddShiftFormData = getDefaultShiftForm();

  timeOptions: string[] = [];
  isSubmitting = false;
  errorMessage = '';
  shiftNameError = '';
  isLoadingShiftData = false;

  // Custom dropdown state
  openStatusDropdown: number | null = null;
  openStartTimeDropdown: number | null = null;
  openEndTimeDropdown: number | null = null;

  constructor(private employeeService: EmployeeService) {
    this.generateTimeOptions();
  }

  /**
   * Handle ngOnChanges lifecycle: clears previous errors and either loads shift details when modal visibility or editShiftId changes in edit mode, populates the form with edit data, or resets the form when not in edit mode.
   * @example
   * gOnChanges({ isVisible: { currentValue: true, previousValue: false }, editShiftId: { currentValue: 123 }, editMode: { currentValue: true } })
   * void
   * @param {SimpleChanges} changes - Object mapping input property names to their SimpleChange values.
   * @returns {void} No value is returned.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Clear any previous error messages when modal opens
    this.errorMessage = '';
    this.shiftNameError = '';

    // Check if modal visibility or edit shift ID changed
    if (changes['isVisible'] && this.isVisible && this.editMode && this.editShiftId) {
      this.loadShiftDetails();
    } else if (changes['editShiftId'] && this.editMode && this.editShiftId && this.isVisible) {
      this.loadShiftDetails();
    } else if (this.editMode && this.editData) {
      this.shiftForm = { ...this.editData };
    } else if (!this.editMode) {
      this.resetForm();
    }
  }

  /**
   * Load shift details for the current editShiftId, populate the form from API data or set an error message and loading state.
   * @example
   * loadShiftDetails()
   * void
   * @returns {{void}} Returns nothing; updates component state (isLoadingShiftData, errorMessage) and populates the form or logs errors.
   **/
  private loadShiftDetails(): void {
    if (!this.editShiftId) return;

    this.isLoadingShiftData = true;
    this.errorMessage = '';

    this.employeeService.getShiftDetails(this.editShiftId).subscribe({
      next: (response: any) => {
        this.isLoadingShiftData = false;
        if (response.status && response.data) {
          this.populateFormFromApiData(response.data);
        } else {
          this.errorMessage = response.message || 'Failed to load shift details.';
        }
      },
      error: (error: any) => {
        this.isLoadingShiftData = false;
        if (error.error && error.error.message) {
          this.errorMessage = error.error.message;
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = 'An error occurred while loading shift details.';
        }
        console.error('Error loading shift details:', error);
      },
    });
  }

  /**
   * Populate the component's shiftForm from API response data, mapping shift and day entries into form-friendly values including status and 12-hour times.
   * @example
   * populateFormFromApiData({ shift: { shift_name: 'Morning' }, days: [{ day_of_week: 'Monday', is_working: true, start_time: '08:00:00', end_time: '16:00:00' }] })
   * undefined
   * @param {{any}} {{data}} - API response object containing a `shift` object and a `days` array used to populate the form.
   * @returns {{void}} Does not return a value; updates the component's shiftForm in place.
   **/
  private populateFormFromApiData(data: any): void {
    const { shift, days } = data;

    // Set shift name
    this.shiftForm.shift_name = shift.shift_name || '';

    // Map API days to form days
    const daysOfWeek = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];

    this.shiftForm.days = daysOfWeek.map(dayName => {
      const apiDay = days.find((d: any) => d.day_of_week === dayName);

      if (apiDay) {
        return {
          day: dayName,
          status: apiDay.is_working ? 'Working' : 'Day off',
          startTime:
            apiDay.is_working && apiDay.start_time
              ? this.convertTimeTo12Hour(apiDay.start_time)
              : '8:00 AM',
          endTime:
            apiDay.is_working && apiDay.end_time
              ? this.convertTimeTo12Hour(apiDay.end_time)
              : '8:00 PM',
        };
      }

      // Default if day not found in API response
      return {
        day: dayName,
        status: 'Day off' as 'Working' | 'Day off',
        startTime: '8:00 AM',
        endTime: '8:00 PM',
      };
    });
  }

  /**
   * Convert a 24-hour time string ("HH:MM:SS") to a 12-hour format with AM/PM.
   * @example
   * convertTimeTo12Hour('20:00:00')
   * '8:00 PM'
   * @param {string} time24 - Time in "HH:MM:SS" 24-hour format.
   * @returns {string} 12-hour time string formatted as "H:MM AM/PM".
   **/
  private convertTimeTo12Hour(time24: string): string {
    // time24 format: "08:00:00" or "20:00:00"
    const [hoursStr, minutesStr] = time24.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    const period = hours >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    if (hours === 0) {
      hours = 12;
    } else if (hours > 12) {
      hours = hours - 12;
    }

    return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  /**
   * Generates 12-hour formatted time strings at 30-minute intervals and appends them to this.timeOptions.
   * @example
   * enenerateTimeOptions()
   * Populates this.timeOptions with 48 entries from '12:00 AM' through '11:30 PM'
   * @returns {{void}} Does not return a value; updates this.timeOptions in place.
   **/
  generateTimeOptions(): void {
    // Generate 12-hour format times with AM/PM (12:00 AM to 11:30 PM)
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const ampm = hour < 12 ? 'AM' : 'PM';
        let displayHour = hour;

        // Convert 24-hour to 12-hour format
        if (hour === 0) {
          displayHour = 12; // 12:00 AM
        } else if (hour > 12) {
          displayHour = hour - 12; // 1:00 PM to 11:30 PM
        }

        const time = `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
        this.timeOptions.push(time);
      }
    }
  }

  onClose(): void {
    this.closeAllDropdowns();
    this.closeModal.emit();
  }

  onModalClick(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Keydown handler to prevent typing beyond 100 characters and show error
   */
  onShiftNameKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;

    // Allow backspace, delete, arrow keys, tab, etc.
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow Ctrl/Cmd combinations (Ctrl+A, Ctrl+C, etc.)
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Check if current value is already at 100 characters
    if (currentValue.length >= 100) {
      // Prevent typing the 101st character
      event.preventDefault();
      this.shiftNameError = 'Shift name cannot exceed 100 characters';
    }
  }

  /**
   * Input handler for shift name field to show error when exceeding 100 characters
   */
  onShiftNameInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Check if trying to exceed 100 characters (in case of paste or other input methods)
    if (value.length > 100) {
      // Truncate to 100 characters
      const truncatedValue = value.substring(0, 100);
      target.value = truncatedValue;
      this.shiftForm.shift_name = truncatedValue;
      this.shiftNameError = 'Shift name cannot exceed 100 characters';
    } else if (value.length === 100) {
      this.shiftNameError = 'Shift name cannot exceed 100 characters';
    } else {
      // Clear error if within limit
      this.shiftNameError = '';
    }
  }

  /**
   * Validate the shift form and save the shift by calling the create or update API, handling success and error states.
   * @example
   * nSave()
   * // void — triggers API request; on success emits saveShift, resets the form, and closes the modal
   * @returns {void} Performs validation and sends create/update request for a shift; does not return a value.
   */
  onSave(): void {
    // Validate shift name length
    if (this.shiftForm.shift_name.length > 100) {
      this.shiftNameError = 'Shift name cannot exceed 100 characters';
      return;
    }

    if (this.shiftForm.shift_name.trim() && !this.isSubmitting) {
      this.isSubmitting = true;
      this.errorMessage = ''; // Clear previous errors
      this.shiftNameError = ''; // Clear shift name errors

      const apiData = this.transformDataForApi();

      // Check if we're in edit mode and have a shift ID
      if (this.editMode && this.editShiftId) {
        // Call update API
        this.employeeService.updateShift(this.editShiftId, apiData).subscribe({
          next: (response: any) => {
            this.isSubmitting = false;
            if (response.status) {
              this.saveShift.emit({ ...this.shiftForm });
              this.resetForm();
              this.onClose();
            } else {
              this.errorMessage = response.message || 'Failed to update shift.';
            }
          },
          error: (error: any) => {
            this.isSubmitting = false;
            // Extract the actual error message from the API response
            if (error.error && error.error.message) {
              this.errorMessage = error.error.message;
            } else if (error.message) {
              this.errorMessage = error.message;
            } else {
              this.errorMessage = 'An error occurred while updating the shift.';
            }
            console.error('Error updating shift:', error);
          },
        });
      } else {
        // Call create API
        this.employeeService.createShift(apiData).subscribe({
          next: (response: any) => {
            this.isSubmitting = false;
            if (response.status) {
              this.saveShift.emit({ ...this.shiftForm });
              this.resetForm();
              this.onClose();
            } else {
              this.errorMessage = response.message || 'Failed to create shift.';
            }
          },
          error: (error: any) => {
            this.isSubmitting = false;
            // Extract the actual error message from the API response
            if (error.error && error.error.message) {
              this.errorMessage = error.error.message;
            } else if (error.message) {
              this.errorMessage = error.message;
            } else {
              this.errorMessage = 'An error occurred while creating the shift.';
            }
            console.error('Error creating shift:', error);
          },
        });
      }
    }
  }

  private transformDataForApi(): CreateShiftRequest {
    return {
      shift_name: this.shiftForm.shift_name,
      days: this.shiftForm.days.map(day => ({
        day_of_week: day.day,
        start_time: day.status === 'Working' ? this.convertTimeTo24Hour(day.startTime) : null,
        end_time: day.status === 'Working' ? this.convertTimeTo24Hour(day.endTime) : null,
        is_working: day.status === 'Working',
      })),
    };
  }

  /**
   * Convert a 12-hour time string (with AM/PM) to a 24-hour HH:mm formatted string.
   * @example
   * convertTimeTo24Hour('02:15 PM')
   * '14:15'
   * @param {{string}} {{time12Hour}} - Time string in 12-hour format, e.g., "02:15 PM" or "12:00 AM".
   * @returns {{string}} 24-hour formatted time string in "HH:mm" format.
   **/
  private convertTimeTo24Hour(time12Hour: string): string {
    const [time, period] = time12Hour.split(' ');
    const [hoursStr, minutesStr] = time.split(':');
    let hoursNum = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (period === 'PM' && hoursNum !== 12) {
      hoursNum += 12;
    } else if (period === 'AM' && hoursNum === 12) {
      hoursNum = 0;
    }

    return `${hoursNum.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  onCancel(): void {
    this.resetForm();
    this.onClose();
  }

  onDayStatusChange(dayIndex: number, status: 'Working' | 'Day off'): void {
    this.shiftForm.days[dayIndex].status = status;
    this.openStatusDropdown = null;

    // Close time dropdowns when status changes to "Day off"
    if (status === 'Day off') {
      this.openStartTimeDropdown = null;
      this.openEndTimeDropdown = null;
    }
  }

  onDayStartTimeChange(dayIndex: number, time: string): void {
    this.shiftForm.days[dayIndex].startTime = time;
    this.openStartTimeDropdown = null;
  }

  onDayEndTimeChange(dayIndex: number, time: string): void {
    this.shiftForm.days[dayIndex].endTime = time;
    this.openEndTimeDropdown = null;
  }

  // Custom dropdown toggle methods
  toggleStatusDropdown(dayIndex: number): void {
    if (this.isSubmitting) return;
    this.openStatusDropdown = this.openStatusDropdown === dayIndex ? null : dayIndex;
    this.openStartTimeDropdown = null;
    this.openEndTimeDropdown = null;
  }

  toggleStartTimeDropdown(dayIndex: number): void {
    if (this.isSubmitting || this.shiftForm.days[dayIndex].status === 'Day off') return;
    this.openStartTimeDropdown = this.openStartTimeDropdown === dayIndex ? null : dayIndex;
    this.openStatusDropdown = null;
    this.openEndTimeDropdown = null;
  }

  toggleEndTimeDropdown(dayIndex: number): void {
    if (this.isSubmitting || this.shiftForm.days[dayIndex].status === 'Day off') return;
    this.openEndTimeDropdown = this.openEndTimeDropdown === dayIndex ? null : dayIndex;
    this.openStatusDropdown = null;
    this.openStartTimeDropdown = null;
  }

  private resetForm(): void {
    this.shiftForm = getDefaultShiftForm();
    this.errorMessage = '';
    this.shiftNameError = '';
    this.closeAllDropdowns();
  }

  private closeAllDropdowns(): void {
    this.openStatusDropdown = null;
    this.openStartTimeDropdown = null;
    this.openEndTimeDropdown = null;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
